import { UserAvatar } from '@deepguide-ai/ui/user-avatar'
import { VeraAvatar } from '@/components/compliance/VeraAvatar'
import { cn } from '@/lib/utils'
import { useAuthenticatedImageUrl } from '@/hooks/useAuthenticatedImageUrl'

type UserAvatarSize = 'xs' | 'sm' | 'md' | 'lg'
type VeraAvatarSize = 20 | 24 | 28 | 32 | 48 | 64

const VERA_SIZE_BY_USER_SIZE: Record<UserAvatarSize, VeraAvatarSize> = {
  xs: 20,
  sm: 24,
  md: 32,
  lg: 48,
}

// VeraAvatar (the agent/service-account branch) doesn't pass through
// UserAvatar, so it needs its own ring to match the silhouette UserAvatar
// applies by default.
const VERA_RING_CLASS = 'ring-1 ring-border'

export interface MemberAvatarPerson {
  name?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  image?: string | null
  isAgent?: boolean | null
  isServiceAccount?: boolean | null
}

interface MemberAvatarProps {
  person?: MemberAvatarPerson | null
  size?: UserAvatarSize
  className?: string
}

export function MemberAvatar({
  person,
  size = 'sm',
  className,
}: MemberAvatarProps) {
  const isAgent = person?.isAgent || person?.isServiceAccount
  const { imageUrl } = useAuthenticatedImageUrl(isAgent ? null : person?.image)
  // Prefer firstName+lastName only when BOTH exist; otherwise `name` is the
  // more complete source. (Session users often carry firstName without
  // lastName, which would derive different initials/colors for the same user
  // than surfaces that pass the full name.)
  const assembledName = [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim()
  const fullName =
    person?.firstName && person?.lastName ? assembledName : person?.name?.trim() || assembledName

  if (isAgent) {
    return (
      <VeraAvatar
        size={VERA_SIZE_BY_USER_SIZE[size]}
        showBadge={false}
        className={cn(VERA_RING_CLASS, className)}
      />
    )
  }

  return (
    <UserAvatar
      image={imageUrl}
      name={fullName}
      email={person?.email}
      size={size}
      className={className}
    />
  )
}
